import { ForumCommentRead as GeneratedForumCommentRead } from './model/forumCommentRead'
import { PaginatedForumCommentReadList } from './model/paginatedForumCommentReadList'
import { ForumTopicReadDetail as GeneratedForumTopicReadDetail } from './model/forumTopicReadDetail'
import { CommentAttachmentList } from './model/commentAttachmentList'
import { ForumSearchResult as GeneratedForumSearchResult } from './model/forumSearchResult'
import { ForumTopicRead } from './model/forumTopicRead'
import { ForumGroup } from './model/forumGroup'
import { PaginatedForumTopicReadList } from './model/paginatedForumTopicReadList'
import { UserWSRetrieveForCommentForum } from './model/userWSRetrieveForCommentForum'

export type ForumTopicParticipant = UserWSRetrieveForCommentForum & {
  isModerator: boolean
  moderatorLocked: boolean
}

export type ForumTopicParticipants = {
  total: number
  pages: number
  canManageModerators: boolean
  results: ForumTopicParticipant[]
}

export type ForumSender = Omit<UserWSRetrieveForCommentForum, 'id' | 'avatar'> & {
  readonly id: number | null
  readonly avatar: string | null
  readonly isGuest?: boolean
  readonly guestId?: number
}

export type ForumTopicListItem = Omit<ForumTopicRead, 'lastComment'> & {
  readonly lastComment: Omit<ForumTopicRead['lastComment'], 'user'> & {
    readonly user?: ForumSender | null
    readonly attachmentsCount?: number
  }
  readonly forumGroups?: ForumGroup[]
  readonly competenceId?: number
}
export type ForumTopicList = Omit<PaginatedForumTopicReadList, 'results'> & { results?: ForumTopicListItem[] }

export type ForumSearchResult = GeneratedForumSearchResult & {
  readonly competenceId?: number
  readonly attachmentsCount?: number
  readonly firstAttachment?: Pick<CommentAttachmentList, 'id' | 'filename' | 'fileType' | 'mediaThumbnail'> | null
}

export type ForumCommentRead = Omit<GeneratedForumCommentRead, 'parent' | 'user'> & {
  readonly user: ForumSender | null
  readonly parent: (Omit<GeneratedForumCommentRead['parent'], 'user'> & { readonly user: ForumSender | null }) | null
  readonly clientId?: string | null
  readonly isEdited?: boolean
  readonly canPin?: boolean
  readonly isPinned?: boolean
  readonly updated?: string
}

export type ForumPinnedComment = {
  readonly id: number
  readonly user?: { readonly authorName?: string } | null
  readonly message?: string
  readonly created?: string
  readonly hasAttachments?: boolean
  readonly attachmentsCount?: number
  readonly attachments?: Array<Pick<CommentAttachmentList, 'id' | 'filename' | 'fileType' | 'mediaThumbnail'>>
  readonly isPinned?: boolean
}

export type ForumDialogCounterpart = ForumSender & {
  readonly transferred?: boolean
  readonly role?: string | null
  readonly competenceName?: string | null
}

export type ForumTopicReadDetail = GeneratedForumTopicReadDetail & {
  readonly canFinish?: boolean
  readonly isGuestChat?: boolean
  readonly isDirect?: boolean
  readonly counterpart?: ForumDialogCounterpart
  readonly pinnedComments: ForumPinnedComment[]
  readonly participantsCount?: number
}

export type ExpertDialog = ForumTopicListItem & {
  canFinish?: boolean
  counterpart: ForumDialogCounterpart
  competenceName: string
}

// Preserve Django's microseconds when comparing versions in different time zones.
export const commentVersion = (value: string) =>
  new Date(value).getTime() * 1000 + Number((value.match(/\.(\d+)/)?.[1] || '').padEnd(6, '0').slice(3, 6))

export type ForumCommentList = Omit<PaginatedForumCommentReadList, 'results'> & {
  results?: ForumCommentRead[]
}

export type CommentWindow = {
  results: ForumCommentRead[]
  olderCursor: string | null
  newerCursor: string | null
  hasOlder: boolean
  hasNewer: boolean
}

export type CommentWindowRequest = {
  topic: number
  limit?: number
  around?: number
  before?: string
  after?: string
  background?: boolean
}
